import React, { useEffect, useRef, useState } from 'react';

import { buscarLinhaDoTempo } from '../../../api/previsaoPecas/previsaoPecasApi';
import { Chegada, EventoLinhaDoTempo, ItemPeca } from '../../../interfaces/PrevisaoPecas';
import { STATUS_DA_PREVISAO, formatarDiaPuro, formatarQuantidade, rotuloDoStatus } from '../formatos';
import BarraDeChegada from './BarraDeChegada';
// Overlay compartilhado dos modais (16+ modais) e a receita do "modal de ações por linha".
import '../../../components/modal/add-car/AddCarModal.css';
import '../../portal_cotacoes/components/ConviteAcoesModal.css';
import '../../../shared/ui/badge-status.css';
import '../../../shared/ui/modal-close-button.css';

interface Props {
  item: ItemPeca;
  /** Abre o pedido inteiro (é lá que se registra e se cancela chegada). */
  onAbrirPedido?: () => void;
  onFechar: () => void;
}

/** "SOAC, pela nota 4521" ou o nome de quem registrou — a tela sempre diz quem (Victor, 24/10/2026). */
export function quemRegistrou(c: Chegada): string {
  if (c.origem === 'NOTA') return `SOAC, pela nota ${c.notaNumero ?? 'sem número'}`;
  return c.registradoPor;
}

/** Uma linha da linha do tempo, em texto. Sem emoji e sem jargão de código. */
function descreverEvento(evento: EventoLinhaDoTempo, unidade: string): React.ReactNode {
  switch (evento.tipo) {
    case 'PEDIDO_CRIADO':
      // `emissao` do pedido no Nasajon é só-data: mostrar hora seria inventar "00:00".
      return `Pedido criado no Nasajon em ${formatarDiaPuro(evento.ocorridoEm)}`;
    case 'PREVISAO_INICIAL':
      return (
        <>
          Previsão inicial do pedido: <strong>{formatarDiaPuro(evento.previsao)}</strong>
        </>
      );
    case 'CHEGADA': {
      const c = evento.chegada;
      return (
        <>
          <span style={c.cancelada ? { textDecoration: 'line-through' } : undefined}>
            Chegaram <strong>{formatarQuantidade(c.quantidade)}</strong> {unidade} em {formatarDiaPuro(c.chegouEm)}
            {c.distribuicao === 'EXCEDENTE' ? ' (acima do pedido)' : ''}
          </span>
          <br />
          <span className="convite-modal-rotulo">
            Registrado por {quemRegistrou(c)}
            {c.cancelada
              ? ` - cancelado por ${c.canceladoPorNome ?? 'não informado'}${c.canceladoMotivo ? `: ${c.canceladoMotivo}` : ''}`
              : ''}
          </span>
        </>
      );
    }
    case 'CHEGADA_PENDENTE':
      return `Faltam ${formatarQuantidade(evento.saldo)} ${unidade} — a chegada é registrada sozinha quando a nota de entrada traz a peça`;
    default:
      return null;
  }
}

/**
 * Uma peça (aba Peças) — aberta pela linha da tabela.
 *
 * ⚠️ As ações FECHAM este modal antes de abrir outro: o filho seria renderizado fora do `ref` deste,
 * e o handler de "clicou fora" o trataria como externo. Quem fecha é a página.
 *
 * 🔴 Falha da linha do tempo NUNCA vira "não houve alteração": afirmar que o prazo nunca mudou por
 * erro de rede seria dizer ao comprador que o fornecedor cumpriu o que prometeu.
 */
const PrevisaoItemModal: React.FC<Props> = ({ item, onAbrirPedido, onFechar }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [eventos, setEventos] = useState<EventoLinhaDoTempo[] | null>(null);
  const [linhaDoTempoFalhou, setLinhaDoTempoFalhou] = useState(false);
  const unidade = item.unidade ?? 'un';

  useEffect(() => {
    let ativo = true;
    buscarLinhaDoTempo(item.id)
      .then((resposta) => {
        if (ativo) setEventos(resposta.eventos);
      })
      .catch(() => {
        // Sem toast: o aviso fica DENTRO do modal, no lugar da lista (ver o bloco abaixo).
        if (ativo) setLinhaDoTempoFalhou(true);
      });
    return () => {
      ativo = false;
    };
  }, [item.id]);

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(evento.target as Node)) onFechar();
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar]);

  const descricao = item.produtoDescricao ?? 'Peça sem descrição';

  return (
    <div className="modal-overlay">
      <div className="convite-modal" ref={modalRef} role="dialog" aria-modal="true" aria-label="Previsão da peça">
        <div className="convite-modal-header">
          <div className="convite-modal-titulo">
            <h2 title={descricao}>{descricao}</h2>
            {item.pedido.numero && <span className="convite-modal-cnpj">- Pedido {item.pedido.numero}</span>}
          </div>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="convite-modal-corpo">
          <section className="convite-modal-secao">
            <h3>Peça</h3>
            <div className="convite-modal-grade">
              <span className="convite-modal-rotulo">Código</span>
              <span>{item.produtoCodigo ?? 'não identificado'}</span>

              <span className="convite-modal-rotulo">Chegada</span>
              <span>
                <BarraDeChegada recebida={item.chegada} pedida={item.pedida} unidade={unidade} largura={90} />
                {item.excedente > 0 ? ` · ${formatarQuantidade(item.excedente)} ${unidade} acima do pedido` : ''}
              </span>

              <span className="convite-modal-rotulo">Veículo</span>
              <span>{item.pedido.placa ?? 'sem placa informada'}</span>

              <span className="convite-modal-rotulo">Oficina</span>
              <span>
                {item.pedido.oficina ?? 'não informada'}
                {item.pedido.oficinaExterna ? ' (oficina externa)' : ''}
              </span>
            </div>
          </section>

          <section className="convite-modal-secao">
            <h3>Pedido</h3>
            <div className="convite-modal-grade">
              <span className="convite-modal-rotulo">Fornecedor</span>
              <span>{item.pedido.fornecedorNome ?? 'não identificado'}</span>

              <span className="convite-modal-rotulo">Número</span>
              <span>{item.pedido.numero ?? 'sem número'}</span>

              <span className="convite-modal-rotulo">Comprador</span>
              <span>{item.pedido.compradorNome ?? 'não identificado'}</span>
            </div>
          </section>

          <section className="convite-modal-secao">
            <h3>Previsão</h3>
            <div className="convite-modal-grade">
              <span className="convite-modal-rotulo">Situação</span>
              <span>
                <span className={`cilia-badge ${STATUS_DA_PREVISAO[item.status].classe}`}>
                  {rotuloDoStatus(item.status)}
                </span>
              </span>

              <span className="convite-modal-rotulo">Previsão de chegada</span>
              <span>
                {formatarDiaPuro(item.previsao)}
              </span>
            </div>
          </section>

          <section className="convite-modal-secao">
            <h3>Linha do tempo</h3>
            {linhaDoTempoFalhou && (
              <p className="convite-modal-erro" role="alert">
                Não foi possível carregar a linha do tempo. Isso não quer dizer que a previsão nunca mudou nem que a
                peça não chegou.
              </p>
            )}
            {!linhaDoTempoFalhou && eventos === null && <p className="convite-modal-vazio">Carregando...</p>}
            {eventos && eventos.length > 0 && (
              <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {eventos.map((evento, indice) => (
                  // Os eventos não têm id próprio, e a ordem vem fixa do servidor.
                  <li key={indice}>{descreverEvento(evento, unidade)}</li>
                ))}
              </ol>
            )}
          </section>

          {onAbrirPedido && (
            <section className="convite-modal-secao">
              <h3>Ações</h3>
              <div className="convite-modal-acoes">
                {onAbrirPedido && (
                  <button type="button" className="convite-modal-acao" onClick={onAbrirPedido}>
                    <span className="convite-modal-acao-rotulo">Abrir o pedido</span>
                    <span className="convite-modal-acao-descricao">
                      Todas as peças do pedido, as notas que chegaram e quem registrou cada chegada.
                    </span>
                  </button>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrevisaoItemModal;
